import { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, Pressable, View } from 'react-native';

import { CurrencyBadge } from '@/components/finance/CurrencyBadge.web';
import { Button, Dialog, DialogButton, Text } from '@/components/ui';
import { distinctLabelColors } from '@/lib/design-system/label-color';
import { fetchMembers } from '@/lib/blocks/api';
import type { MemberRecord } from '@/lib/blocks/types';
import {
  createAccount,
  deleteAccount,
  fetchAccounts,
  fetchCurrencies,
  updateAccount,
  type AccountInput,
} from '@/lib/finance/api';
import type { Account, Currency } from '@/lib/finance/types';

type FormState = {
  name: string;
  institution: string;
  accountNumber: string;
  currencyCode: string;
  ownerMemberId: string;
  isActive: boolean;
  sortOrder: string;
};

const EMPTY_FORM: FormState = {
  name: '',
  institution: '',
  accountNumber: '',
  currencyCode: '',
  ownerMemberId: '',
  isActive: true,
  sortOrder: '0',
};

export function AccountsEditor() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [members, setMembers] = useState<MemberRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [editing, setEditing] = useState<Account | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [accountRows, currencyRows, memberRows] = await Promise.all([
        fetchAccounts(),
        fetchCurrencies(),
        fetchMembers(),
      ]);
      setAccounts(accountRows);
      setCurrencies(currencyRows);
      setMembers(memberRows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const currencyColors = useMemo(
    () => distinctLabelColors(currencies.map((c) => c.code)),
    [currencies],
  );

  function memberName(id: string | null): string | null {
    if (!id) return null;
    return members.find((m) => m.id === id)?.displayName ?? null;
  }

  function openCreate() {
    setEditing(null);
    setForm({ ...EMPTY_FORM, currencyCode: currencies[0]?.code ?? '' });
    setError(null);
    setDialogOpen(true);
  }

  function openEdit(account: Account) {
    setEditing(account);
    setForm({
      name: account.name,
      institution: account.institution ?? '',
      accountNumber: account.accountNumber ?? '',
      currencyCode: account.currencyCode,
      ownerMemberId: account.ownerMemberId ?? '',
      isActive: account.isActive,
      sortOrder: String(account.sortOrder),
    });
    setError(null);
    setDialogOpen(true);
  }

  async function onSave() {
    const name = form.name.trim();
    if (!name) {
      setError('Name is required');
      return;
    }
    if (!form.currencyCode) {
      setError('Currency is required');
      return;
    }
    const payload: AccountInput = {
      name,
      institution: form.institution.trim() || null,
      accountNumber: form.accountNumber.trim() || null,
      currencyCode: form.currencyCode,
      ownerMemberId: form.ownerMemberId || null,
      isActive: form.isActive,
      sortOrder: Number(form.sortOrder) || 0,
    };
    setBusy(true);
    setError(null);
    try {
      if (editing) {
        await updateAccount(editing.id, payload);
      } else {
        await createAccount(payload);
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save account');
    } finally {
      setBusy(false);
    }
  }

  async function onDelete(account: Account) {
    if (!window.confirm(`Delete account "${account.name}"? It will be hidden from new entries.`)) {
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await deleteAccount(account.id);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete account');
    } finally {
      setBusy(false);
    }
  }

  return (
    <View>
      <View className="mb-4 flex-row items-center">
        <Text variant="h3">Accounts</Text>
        <View className="flex-1" />
        <Button
          label="Add account"
          size="sm"
          variant="primary"
          disabled={busy || currencies.length === 0}
          onPress={openCreate}
        />
      </View>

      {currencies.length === 0 ? (
        <Text variant="body-sm" className="mb-3 text-fg-muted">
          Add at least one currency first (Currencies tab).
        </Text>
      ) : null}

      {error && !dialogOpen ? (
        <Text variant="body-sm" className="mb-3 text-danger">
          {error}
        </Text>
      ) : null}

      {loading ? (
        <ActivityIndicator />
      ) : accounts.length === 0 ? (
        <Text variant="body" className="text-muted">
          No accounts yet.
        </Text>
      ) : (
        <View className="rounded-lg border border-border">
          {accounts.map((account, index) => (
            <View
              key={account.id}
              className={`flex-row items-center gap-3 px-3 py-2 ${
                index > 0 ? 'border-t border-border' : ''
              }`}
            >
              <CurrencyBadge
                code={account.currencyCode}
                color={currencyColors.get(account.currencyCode)}
              />
              <View className="min-w-0 flex-1">
                <View className="flex-row items-center gap-2">
                  <Text variant="body" className="font-medium">
                    {account.name}
                  </Text>
                  {!account.isActive ? (
                    <Text variant="caption" className="text-muted">
                      (inactive)
                    </Text>
                  ) : null}
                </View>
                <Text variant="caption" className="text-muted">
                  {[account.institution, account.accountNumber, memberName(account.ownerMemberId)]
                    .filter(Boolean)
                    .join(' · ') || '—'}
                </Text>
              </View>
              <Pressable onPress={() => openEdit(account)}>
                <Text variant="caption" className="text-accent">
                  Edit
                </Text>
              </Pressable>
              <Pressable onPress={() => void onDelete(account)}>
                <Text variant="caption" className="text-danger">
                  Delete
                </Text>
              </Pressable>
            </View>
          ))}
        </View>
      )}

      <Dialog
        open={dialogOpen}
        title={editing ? 'Edit account' : 'New account'}
        onClose={() => setDialogOpen(false)}
        footer={
          <>
            <DialogButton label="Cancel" variant="secondary" onPress={() => setDialogOpen(false)} />
            <DialogButton
              label={busy ? 'Saving…' : 'Save'}
              variant="primary"
              disabled={busy}
              onPress={() => void onSave()}
            />
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1">
            <span className="cs-text-label">Name</span>
            <input
              className="cs-input"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="cs-text-label">Institution</span>
            <input
              className="cs-input"
              value={form.institution}
              onChange={(e) => setForm((f) => ({ ...f, institution: e.target.value }))}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="cs-text-label">Account number</span>
            <input
              className="cs-input"
              value={form.accountNumber}
              onChange={(e) => setForm((f) => ({ ...f, accountNumber: e.target.value }))}
            />
            <span className="cs-text-caption text-muted">
              Last 4 or the number printed on statements is enough.
            </span>
          </label>
          <label className="flex flex-col gap-1">
            <span className="cs-text-label">Currency</span>
            <select
              className="cs-input"
              value={form.currencyCode}
              onChange={(e) => setForm((f) => ({ ...f, currencyCode: e.target.value }))}
            >
              {currencies.map((currency) => (
                <option key={currency.code} value={currency.code}>
                  {currency.code}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="cs-text-label">Owner (optional)</span>
            <select
              className="cs-input"
              value={form.ownerMemberId}
              onChange={(e) => setForm((f) => ({ ...f, ownerMemberId: e.target.value }))}
            >
              <option value="">Shared / none</option>
              {members.map((member) => (
                <option key={member.id} value={member.id}>
                  {member.displayName}
                </option>
              ))}
            </select>
          </label>
          <div className="flex flex-row items-center gap-4">
            <label className="flex flex-row items-center gap-2">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
              />
              <span className="cs-text-label">Active</span>
            </label>
            <label className="flex flex-col gap-1">
              <span className="cs-text-label">Sort order</span>
              <input
                className="cs-input w-24"
                inputMode="numeric"
                value={form.sortOrder}
                onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
              />
            </label>
          </div>
          {error && dialogOpen ? (
            <span className="cs-text-caption text-danger">{error}</span>
          ) : null}
        </div>
      </Dialog>
    </View>
  );
}
